'use client'

import { useEffect, useRef, RefObject } from 'react'

interface UseScrollAnimationOptions {
  threshold?: number
  rootMargin?: string
  triggerOnce?: boolean
  animateChildren?: boolean
}

/**
 * Hook for triggering scroll-based animations using IntersectionObserver
 *
 * @example
 * ```tsx
 * const sectionRef = useScrollAnimation()
 *
 * return (
 *   <section ref={sectionRef} className="animate-on-scroll">
 *     Content that fades in on scroll
 *   </section>
 * )
 * ```
 */
export function useScrollAnimation<T extends HTMLElement = HTMLElement>(
  options: UseScrollAnimationOptions = {},
): RefObject<T | null> {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true, animateChildren = false } = options
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')

            // Also trigger animations on children if specified
            if (animateChildren) {
              entry.target.querySelectorAll('.animate-on-scroll').forEach(child => {
                child.classList.add('is-visible')
              })
            }

            // Unobserve if we only want to trigger once
            if (triggerOnce) {
              observer.unobserve(entry.target)
            }
          } else if (!triggerOnce) {
            entry.target.classList.remove('is-visible')
            if (animateChildren) {
              entry.target.querySelectorAll('.animate-on-scroll').forEach(child => {
                child.classList.remove('is-visible')
              })
            }
          }
        })
      },
      { threshold, rootMargin },
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [threshold, rootMargin, triggerOnce, animateChildren])

  return ref
}
